import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, TrendingUp, Calendar } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getResumoFinanceiro } from "../services/relatorios";

export function Relatorios() {
  const { can } = useAuth();
  const hoje = new Date().toISOString().split("T")[0];
  const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];
  const [d0, setD0] = useState(inicioMes);
  const [d1, setD1] = useState(hoje);

  const { data: finData } = useQuery({
    queryKey: ["relatorio-financeiro", d0, d1],
    queryFn: () => getResumoFinanceiro(d0, d1),
    enabled: can(["gestor", "admin"]),
  });

  if (!can(["gestor", "admin"])) {
    return <div className="text-slate-500">Acesso restrito.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <TrendingUp size={22} className="text-blue-600" /> Relatório financeiro
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Resumo dos recebimentos no período.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2">
          <Calendar size={16} className="text-slate-400" />
          <input type="date" value={d0} onChange={(e) => setD0(e.target.value)} className="text-sm px-2 py-1 outline-none" />
          <span className="text-slate-400">a</span>
          <input type="date" value={d1} onChange={(e) => setD1(e.target.value)} className="text-sm px-2 py-1 outline-none" />
        </div>
      </div>

      {finData && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm">Total Recebido</p>
              <p className="text-2xl font-bold text-green-600 tabular-nums whitespace-nowrap">
                R$ {Number(finData.totalRecebido || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm">Pagamentos</p>
              <p className="text-2xl font-bold text-slate-800">{finData.totalPagamentos}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm">Contratos Atrasados</p>
              <p className="text-2xl font-bold text-red-600">{finData.contratosAtrasados}</p>
            </div>
          </div>

          {finData.pagamentosPorProjeto && Object.keys(finData.pagamentosPorProjeto).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 size={18} /> Recebimentos por projeto</h3>
              <div className="space-y-3">
                {Object.entries(finData.pagamentosPorProjeto as Record<string, number>).map(([nome, valor]) => (
                  <div key={nome} className="flex items-center gap-4">
                    <div className="w-48 text-sm truncate" title={nome}>{nome}</div>
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                      <div className="bg-green-500 h-full rounded-full transition-all" style={{ width: `${Math.min(100, (valor / finData.totalRecebido) * 100)}%` }} />
                    </div>
                    <div className="w-24 text-right text-sm font-medium tabular-nums">R$ {Number(valor).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
